/**
 * SB2-W-09 (2026-05-20) — country-filter search index.
 *
 * Christian, transcript [00:05:18]–[00:05:31]:
 *
 *   *"if I enter Austria so the complete name I do not see any
 *   options and I expect if a user is entering the complete name of
 *   the country that also the search is done in both so in the
 *   complete country name and we also search in the alpha-2 codes and
 *   in the back end or in the database we also have the alpha-3
 *   codes available theoretically it's also possible to search in
 *   alpha-2 and alpha-3 and the entire country name."*
 *
 * OQ-D2(a) lock: SB2 ships English + alpha-2 + alpha-3 only;
 * localised names (Österreich, Deutschland, …) deferred.
 *
 * The Sites page renders one chip option per backend-facet
 * `CountryFacetItem`. The visible chip label is
 * `${alpha2} - ${englishName}` (e.g. "DE - Germany"), so MUI
 * Autocomplete's default substring filter already matches typed input
 * against alpha-2 + English name. The piece it misses is alpha-3 —
 * "AUT" / "DEU" / "GBR" — which never appear in the label. This
 * matcher fills that gap by substring-matching across all three
 * identifiers.
 *
 * Match policy: case-insensitive substring, after `.trim()`. Empty
 * input matches everything (the filter is a no-op).
 */
import type { CountryFacetItem } from "../queries/useCountryFacet.js";
import { toAlpha2 } from "./iso3166.js";

/**
 * Minimal shape: anything carrying any of the three searchable
 * identifiers will match. The Sites page builds this from the
 * `CountryFacetItem` + the local iso3166 fallback (`countryByCode`).
 */
export interface CountrySearchEntry {
  /** Canonical filter token — what the FE stores in `?country=` and
   *  passes to the BE via the listSites `country` CSV param. Alpha-2
   *  per `assets/v1@2.0.0` (FEAT-010). */
  code: string;
  /** ISO 3166-1 alpha-2 (e.g. "DE"). Always present at SB2-W-04+. */
  alpha2?: string | undefined;
  /** ISO 3166-1 alpha-3 (e.g. "DEU"). Populated by the 4.4.0 BE. */
  alpha3?: string | undefined;
  /** Canonical English short name (e.g. "Germany"). */
  englishName?: string | undefined;
}

/**
 * Case-insensitive substring match across alpha-2, alpha-3, and the
 * English short name. Empty / whitespace-only query is treated as a
 * universal match.
 *
 * Example matches for the Austria entry:
 *   - "AT", "at"       → alpha-2 substring
 *   - "AUT", "aut"     → alpha-3 substring
 *   - "Austria", "aust", "AUSTRIA" → English-name substring
 *   - "Österreich"     → no match (deferred per OQ-D2(a))
 */
export function matchesCountrySearch(
  entry: CountrySearchEntry,
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return true;
  if (entry.alpha2?.toLowerCase().includes(q) === true) return true;
  if (entry.alpha3?.toLowerCase().includes(q) === true) return true;
  if (entry.englishName?.toLowerCase().includes(q) === true) return true;
  // Last-ditch — match against the raw filter code (covers tenants
  // whose code isn't a real ISO alpha-2 yet, e.g. legacy seed rows).
  if (entry.code.toLowerCase().includes(q)) return true;
  return false;
}

/**
 * Build a `CountrySearchEntry` from a backend-facet item. Falls back
 * to `toAlpha2(code)` when the facet didn't ship alpha-2 (pre-4.4.0
 * BE), and leaves alpha-3 / englishName undefined when absent (the
 * matcher then just skips those branches).
 */
export function entryFromFacetItem(item: CountryFacetItem): CountrySearchEntry {
  return {
    code: item.countryCode,
    alpha2: item.alpha2 ?? toAlpha2(item.countryCode),
    alpha3: item.alpha3,
    englishName: item.englishName,
  };
}
